import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'
import { Button } from '@wmgtech/legato'

import { GET_USER_FOLLOWS } from 'modulesCommon/graphql/queries'
import { useFollowProject, useUnfollowProject } from 'modulesCommon/graphql/mutations'

import { isProjectFollowed } from 'shared/utils/follows'

import { FollowButton } from 'modulesCommon/Follow/components/index'
import { notificationMessageVar, userFollowsVar } from 'modulesCommon/store'

import { useLoading } from 'shared/hooks'
import { cn } from 'shared/utils'

import { IFollowButtonProps } from './FollowButton.props'
import { useCurrentUser } from '../../../modules/User/useCurrentUser'

const FollowButtonContainer: FC<IFollowButtonProps> = ({
  projectId,
  className,
  userId,
  dataTestPrefix,
  projectName,
  disabled
}) => {
  const { pathname } = useLocation()
  const [userFollowed, setFollowed] = useState(false)
  const { user } = useCurrentUser()

  const isProjectOwner = useMemo(() => `${userId}` === `${user?.id}`, [user, userId])
  const isGlobalize = useMemo(() => pathname.includes('/globalhub'), [pathname])

  const { data: followsData } = useQuery(GET_USER_FOLLOWS)

  const { mutate: followProject, loading: followProjectLoading } = useFollowProject(projectId)
  const { mutate: unfollowProject, loading: unfollowProjectLoading } = useUnfollowProject(projectId)

  const toggleFollow = useCallback(
    (isFollow: boolean) => {
      setFollowed(isFollow)
      notificationMessageVar({
        type: 'success',
        title: 'Success',
        message: (
          <>
            You have successfully {isFollow ? 'followed' : 'unfollowed'} the project <b>{projectName}</b>.
          </>
        )
      })
    },
    [projectName]
  )

  const followHandler = useCallback(() => {
    followProject().then(() => toggleFollow(true))
  }, [followProject, toggleFollow])

  const unfollowHandler = useCallback(() => {
    unfollowProject().then(() => toggleFollow(false))
  }, [unfollowProject, toggleFollow])

  useEffect(() => {
    if (followsData) {
      userFollowsVar(followsData.getUserFollows)
      setFollowed(isProjectFollowed(projectId, followsData.getUserFollows))
    }
  }, [projectId, followsData])

  useLoading({ isLoading: followProjectLoading || unfollowProjectLoading })

  return followsData && !isProjectOwner ? (
    <FollowButton
      className={cn(className, { 'global-hub': isGlobalize })}
      isFollowed={userFollowed}
      followProject={followHandler}
      unfollowProject={unfollowHandler}
      dataTestPrefix={dataTestPrefix}
      disabled={disabled}
    />
  ) : isProjectOwner ? (
    <Button
      className={cn('follow-button follow-button--owner', { 'global-hub': isGlobalize, disabled })}
      colorType="success"
      label="Owner"
      disabled={disabled}
      size="sm"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    />
  ) : null
}

export default FollowButtonContainer
